import { getApiBaseUrl } from '../lib/storefront/storeBootstrap';
import {
    ENTRY_REFERRER,
    ENTRY_UTM,
    getVisitorId,
    readSessionId,
    writeSessionId,
} from '../lib/storefront/analyticsIdentity';

// Deviates from the axios idiom used elsewhere in src/Api on purpose:
// - fetch, because axios goes through XHR in the browser and cannot set referrerPolicy,
//   which this beacon depends on to be attributed correctly.
// - getApiBaseUrl(), because a missing REACT_APP_API_BASE_URL must not turn into a POST to
//   "undefined/api/...".

const REQUEST_TIMEOUT_MS = 8000;
const SUPPRESS_RATE_LIMITED_MS = 10 * 60 * 1000;
const SUPPRESS_MISCONFIGURED_MS = 30 * 60 * 1000;
const SUPPRESS_TRANSIENT_MS = 60 * 1000;
const MAX_CONSECUTIVE_FAILURES = 5;

// Module scope so the breaker and the send order survive a StrictMode remount and the
// StoreProvider unmounting its children on a store switch.
let sendChain = Promise.resolve(null);
let suppressUntil = 0;
let consecutiveFailures = 0;
let disabled = false;

function warn(message) {
    // import.meta.env is Vite syntax and does not compile under react-scripts.
    if (process.env.NODE_ENV !== 'production') {
        console.warn(`[PageViewBeacon] ${message}`);
    }
}

function recordFailure(suppressMs, message) {
    consecutiveFailures += 1;
    suppressUntil = Date.now() + suppressMs;
    warn(message);

    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        disabled = true;
        warn('giving up for the rest of this page load');
    }
}

function buildPayload(page) {
    const visitorId = getVisitorId();
    const sessionId = readSessionId();
    const payload = { page };

    // Omitted rather than nulled when storage is blocked: the server then derives an id
    // from ip + user agent instead of treating a null as an identity.
    if (visitorId) payload.visitorId = visitorId;

    if (sessionId) payload.sessionId = sessionId;

    // Sent on every beacon, not just the session's first. The session pins its channel on
    // creation and keeps it, but the visitor document is $set unconditionally on each
    // pageview — so omitting these later would rewrite a visitor who arrived from Instagram
    // back to source 'direct' with an empty referrer.
    if (ENTRY_REFERRER) payload.referrer = ENTRY_REFERRER;
    if (ENTRY_UTM) payload.utm = ENTRY_UTM;

    return payload;
}

async function doSend(page, storeId) {
    if (disabled) return null;
    if (Date.now() < suppressUntil) return null;
    // An optimisation only — captive portals report true while dropping every request.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
        const response = await fetch(`${getApiBaseUrl()}/api/analytics/pageview`, {
            method: 'POST',
            keepalive: true,
            credentials: 'omit',
            // Load-bearing, not hygiene: an absent body referrer falls back to the Referer
            // header server-side, which would bucket every pageview as a referral from our
            // own domain instead of direct.
            referrerPolicy: 'no-referrer',
            headers: {
                'Content-Type': 'application/json',
                'X-Store-Id': storeId,
            },
            body: JSON.stringify(buildPayload(page)),
            signal: controller.signal,
        });

        // Branch on status, never on body.success — the store-resolution 400 has no such key.
        if (response.status === 201) {
            consecutiveFailures = 0;
            const body = await response.json().catch(() => null);
            if (body && body.sessionId) writeSessionId(body.sessionId);
            return body;
        }

        if (response.status === 429) {
            // Jittered: a restaurant's customers share one NAT address, so an unjittered
            // window would release every client back at the same instant.
            recordFailure(
                SUPPRESS_RATE_LIMITED_MS * (0.75 + Math.random() * 0.5),
                'rate limited, pausing',
            );
            return null;
        }

        if (response.status === 400) {
            // Store context is wrong; that will not fix itself on the next page.
            recordFailure(SUPPRESS_MISCONFIGURED_MS, 'rejected as a bad request, pausing');
            return null;
        }

        recordFailure(SUPPRESS_TRANSIENT_MS, `unexpected status ${response.status}`);
        return null;
    } catch (error) {
        // Never retried: the endpoint creates a page view unconditionally and offers no
        // update path, so a retry after a lost response double-counts permanently. A
        // dropped beacon is an unbiased undercount; a duplicated one is not.
        recordFailure(
            SUPPRESS_TRANSIENT_MS,
            error && error.name === 'AbortError' ? 'timed out' : 'network error',
        );
        return null;
    } finally {
        clearTimeout(timer);
    }
}

/** Fire-and-forget pageview beacon; never throws and never retries — do not await it. */
export function sendPageView(page, storeId) {
    // Serialized so the first response can store its session id before the next beacon
    // reads one. Two concurrent beacons would both arrive without a session and the server
    // would create a session for each.
    sendChain = sendChain.then(() => doSend(page, storeId)).catch(() => null);
    return sendChain;
}
